import { FC } from 'react'
import { ArrowRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'

const defectMeta: CardMeta = {
  description: 'Active defects and observations logged across the fleet.',
  endpoint: 'GET /qhse/defects?activeOnly=true',
  logic: 'Displays recent 5 defects sorted by dateObserved descending. Click "View all" for full list.',
}

const severityColor = (cat?: string | null) => {
  if (!cat) return 'bg-slate-100 text-slate-600'
  if (cat.includes('CRITICAL') || cat === 'THIRD_PARTY_INSPECTION') return 'bg-red-50 text-red-600'
  if (cat === 'SHIP_OBSERVATION') return 'bg-amber-50 text-amber-600'
  return 'bg-blue-50 text-blue-600'
}

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface Props {
  defects: any[]
  onViewAll: () => void
}

const DefectsOverview: FC<Props> = ({ defects, onViewAll }) => {
  const recent = defects.slice(0, 5)

  return (
    <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <h6 className='text-sm font-bold text-slate-900'>Defects Overview</h6>
          <CardInfoButton meta={defectMeta} />
        </div>
        <button onClick={onViewAll} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors'>
          View all <ArrowRight className='w-3 h-3' />
        </button>
      </div>
      <div className='overflow-x-auto max-h-[280px] overflow-y-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
              <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Description</th>
              <th className='text-left font-semibold py-2 px-2'>Category</th>
              <th className='text-left font-semibold py-2 px-2'>Date</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {recent.length === 0 ? (
              <tr>
                <td colSpan={5} className='text-center text-slate-400 py-8 text-xs'>No defects found</td>
              </tr>
            ) : (
              recent.map((d, i) => (
                <tr key={d.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{d.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700 max-w-[200px] truncate'>{d.description || '—'}</td>
                  <td className='py-2.5 px-2'>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${severityColor(d.category)}`}>
                      {d.category || '—'}
                    </span>
                  </td>
                  <td className='py-2.5 px-2 text-slate-500'>{fmtDate(d.dateObserved)}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      d.status === 'CLOSE' || d.status === 'REVIEWED' ? 'bg-emerald-50 text-emerald-600' :
                      d.status === 'SUBMIT' ? 'bg-amber-50 text-amber-600' :
                      'bg-slate-50 text-slate-600'
                    }`}>{d.status || 'SAVE'}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DefectsOverview
